import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { InstallBanner } from "@/components/InstallPrompt";

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Logged in");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <InstallBanner />
      <div className="flex-1 grid lg:grid-cols-2">
      {/* LEFT: brand + image */}
      <div className="relative hidden lg:block noise">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1610891015188-5369212db097?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHx0ZXh0aWxlJTIwbWFudWZhY3R1cmluZyUyMGZhY3Rvcnl8ZW58MHx8fHwxNzg3NzUxNjc3fDA&ixlib=rb-4.1.0&q=85)",
          }}
        />
        <div className="absolute inset-0 bg-[#0a1330]/90" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 border-2 border-white flex items-center justify-center">
                <div className="w-2 h-2 bg-white" />
              </div>
              <div className="label-caps text-white/60">Manufacturing OS</div>
            </div>
            <div className="mt-10 relative">
              <div className="absolute -left-3 top-0 bottom-0 w-[3px] bg-white" />
              <div className="font-display font-black text-[9rem] leading-[0.85] tracking-[-0.06em]">
                SLKC
              </div>
              <div className="mt-4 flex items-center gap-3">
                <div className="h-[1px] w-12 bg-white/60" />
                <div className="label-caps text-white/70">Selvalakshmi · Knits &amp; Co.</div>
              </div>
              <div className="mt-6 max-w-sm text-sm text-white/70 leading-relaxed">
                Inner garment manufacturing — from yarn intake to final dispatch, every kilo and every piece accounted for.
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-4">
              {["YARN", "KNIT", "DYE", "COMPACT"].map((s) => (
                <div key={s}>
                  <div className="label-caps text-white/50">{s}</div>
                  <div className="mt-1 h-[2px] bg-white/60" />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-4 gap-4">
              {["CUT", "STITCH", "PACK", "DISPATCH"].map((s) => (
                <div key={s}>
                  <div className="label-caps text-white/50">{s}</div>
                  <div className="mt-1 h-[2px] bg-white/60" />
                </div>
              ))}
            </div>
            <div className="pt-4 border-t border-white/15 text-[10px] font-mono text-white/50 tracking-widest uppercase">
              v1.0 · Kg → Pcs · INR
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT: form */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="label-caps mb-3">Sign In</div>
          <h1 className="font-display font-black text-4xl leading-none">
            Welcome back.
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Track every kilo and every piece from yarn to dispatch.
          </p>

          <div className="mt-10 space-y-5">
            <div>
              <div className="label-caps mb-2">Email</div>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-11 px-3 bg-white border border-border focus:border-foreground focus:outline-none font-mono text-sm"
              />
            </div>
            <div>
              <div className="label-caps mb-2">Password</div>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-11 px-3 bg-white border border-border focus:border-foreground focus:outline-none font-mono text-sm"
              />
            </div>
            <button
              data-testid="login-submit"
              disabled={loading}
              className="w-full h-11 bg-foreground text-background font-mono text-xs tracking-[0.2em] uppercase font-bold hover:-translate-y-[1px] transition-transform disabled:opacity-50"
            >
              {loading ? "Signing in…" : "Enter Dashboard →"}
            </button>
          </div>
        </form>
      </div>
      </div>
    </div>
  );
}
